/**
 * Attendance roster — month selector + per-staff day rows with mono in/out.
 * Wired: GET /api/services/staff + GET /api/services/staff/<id>/attendance?month=YYYY-MM.
 * Port of screens-services.jsx Attendance.
 */
import { router, useLocalSearchParams } from 'expo-router';
import React from 'react';
import { View } from 'react-native';

import { space } from '@bero/tokens';
import { extractItems, fmtTime, str } from '@/components/gate/api';
import { Icon } from '@/components/gate/icons';
import { useT, monthLong, dayShort } from '@/i18n';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, Card, EmptyState, Hairline, IconButton, Screen, Skeleton, TopBar } from '@/ui';

// full month names via i18n monthLong (localised with active language)

/** Arch-shaped avatar — the design's staff motif. */
function ArchAvatar({ name, size = 36 }: { name: string; size?: number }) {
  const { colors } = useTheme();
  const initials = name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  return (
    <View style={{
      width: size, height: size * 1.15,
      borderTopLeftRadius: size / 2, borderTopRightRadius: size / 2,
      borderBottomLeftRadius: size * 0.11, borderBottomRightRadius: size * 0.11,
      backgroundColor: colors.primary100, alignItems: 'center', justifyContent: 'center',
    }}>
      <AppText weight="700" style={{ color: colors.primary900, fontSize: size * 0.3 }}>{initials}</AppText>
    </View>
  );
}

type DayRow = { day: string; inAt?: string; outAt?: string };
type Roster = { id: string; name: string; role: string; days: DayRow[] };

function toDayRow(d: unknown): DayRow {
  const iso = str(d, 'date', 'day');
  let label = iso ?? '';
  if (iso && /^\d{4}-\d{2}-\d{2}/.test(iso)) {
    const dt = new Date(iso);
    if (!Number.isNaN(dt.getTime())) {
      label = `${dayShort(dt.getDay())} ${dt.getDate()}`;
    }
  }
  const cleanTime = (v?: string) => {
    if (!v || v === '—') return undefined;
    const parsed = new Date(v);
    return Number.isNaN(parsed.getTime()) ? v : fmtTime(parsed);
  };
  return {
    day: label || '—',
    inAt: cleanTime(str(d, 'in', 'inAt', 'inTime')),
    outAt: cleanTime(str(d, 'out', 'outAt', 'outTime')),
  };
}

/** In/out pill with tabular mono time (design .pill--resolved / .pill--pending). */
function TimePill({ kind, time }: { kind: 'in' | 'out'; time: string }) {
  const { colors } = useTheme();
  const bg = kind === 'in' ? colors.success50 : colors.canvas2;
  const fg = kind === 'in' ? colors.success700 : colors.textTertiary;
  return (
    <View style={{
      backgroundColor: bg, borderRadius: 10, paddingHorizontal: space.s2, paddingVertical: 2,
      flexDirection: 'row', alignItems: 'center', gap: 4,
    }}>
      <AppText variant="label" style={{ color: fg }}>{kind === 'in' ? 'IN' : 'OUT'}</AppText>
      <AppText variant="bodySm" mono style={{ color: fg }}>{time}</AppText>
    </View>
  );
}

export default function AttendanceScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const params = useLocalSearchParams<{ staffId?: string; name?: string }>();

  const now = new Date();
  const [month, setMonth] = React.useState({ y: now.getFullYear(), m: now.getMonth() });
  const [rosters, setRosters] = React.useState<Roster[]>([]);
  const [state, setState] = React.useState<'loading' | 'ready' | 'error'>('loading');

  const monthKey = `${month.y}-${String(month.m + 1).padStart(2, '0')}`;
  const isCurrentMonth = month.y === now.getFullYear() && month.m === now.getMonth();

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setState('loading');
      try {
        let members: { id: string; name: string; role: string }[] = [];
        try {
          const body = await api.get<unknown>('/api/services/staff');
          members = extractItems<unknown>(body, 'staff').map((s, i) => ({
            id: str(s, 'id') ?? `s${i}`,
            name: str(s, 'name', 'fullName') ?? 'Staff member',
            role: str(s, 'role') ?? 'Help',
          }));
        } catch {
          // staff list unavailable — fall through to the single-id path
        }
        if (params.staffId) {
          const found = members.find((m) => m.id === String(params.staffId));
          members = [found ?? { id: String(params.staffId), name: params.name || 'Staff member', role: 'Help' }];
        }
        if (members.length === 0) {
          if (!cancelled) { setRosters([]); setState('error'); }
          return;
        }
        let failures = 0;
        const results = await Promise.all(members.map(async (m) => {
          try {
            const body = await api.get<unknown>(`/api/services/staff/${encodeURIComponent(m.id)}/attendance?month=${monthKey}`);
            const days = extractItems<unknown>(body, 'attendance', 'days', 'log').map(toDayRow);
            return { ...m, days } as Roster;
          } catch {
            failures += 1;
            return { ...m, days: [] } as Roster;
          }
        }));
        if (!cancelled) {
          if (failures === results.length) {
            setRosters([]);
            setState('error');
          } else {
            setRosters(results.filter((r) => r.days.length > 0));
            setState('ready');
          }
        }
      } catch {
        if (!cancelled) { setRosters([]); setState('error'); }
      }
    })();
    return () => { cancelled = true; };
  }, [monthKey, params.staffId, params.name]);

  const shift = (dir: -1 | 1) => {
    setMonth((cur) => {
      const d = new Date(cur.y, cur.m + dir, 1);
      return { y: d.getFullYear(), m: d.getMonth() };
    });
  };

  return (
    <Screen scroll={false}>
      <TopBar title={t('at_title')} onBack={() => router.back()} />
      <Screen padded style={{ paddingTop: space.s4 }}>
        {/* month selector */}
        <View style={{
          flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: space.s4,
        }}>
          <IconButton label={t('at_prev_month')} size={44} onPress={() => shift(-1)}>
            <Icon name="chevron-left" size={20} color={colors.textPrimary} />
          </IconButton>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2 }}>
            <Icon name="calendar" size={16} color={colors.textTertiary} />
            <AppText variant="bodyMd" weight="500">{monthLong(month.m)} {month.y}</AppText>
          </View>
          <IconButton label={t('at_next_month')} size={44} onPress={() => !isCurrentMonth && shift(1)}>
            <Icon name="chevron-right" size={20} color={isCurrentMonth ? colors.borderFirm : colors.textPrimary} />
          </IconButton>
        </View>

        {state === 'loading' ? (
          <View style={{ gap: space.s4 }}>
            {[0, 1].map((i) => <Skeleton key={i} h={200} />)}
          </View>
        ) : rosters.length === 0 ? (
          <EmptyState
            icon={<Icon name="clock" size={40} color={colors.textTertiary} />}
            title={state === 'error' ? t('at_err_title') : t('at_empty_title')}
            body={state === 'error' ? t('at_err_body') : t('at_empty_body', { month: monthLong(month.m) })}
          />
        ) : (
          <View style={{ gap: space.s4 }}>
            {rosters.map((r) => (
              <Card key={r.id}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                  <ArchAvatar name={r.name} size={36} />
                  <View>
                    <AppText variant="bodyMd" weight="500">{r.name}</AppText>
                    <AppText variant="bodySm" tone="tertiary">{r.role}</AppText>
                  </View>
                </View>
                <View style={{ marginTop: space.s3 }}>
                  {r.days.length === 0 ? (
                    <AppText variant="bodySm" tone="tertiary">{t('at_no_records')}</AppText>
                  ) : r.days.map((d, j) => (
                    <View key={j}>
                      {j > 0 ? <Hairline /> : null}
                      <View style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: space.s2, gap: space.s2, flexWrap: 'wrap' }}>
                        <AppText variant="bodyMd" tone="secondary" style={{ width: 64 }}>{d.day}</AppText>
                        <View style={{ flex: 1, flexDirection: 'row', flexWrap: 'wrap', gap: space.s2 }}>
                          {d.inAt ? <TimePill kind="in" time={d.inAt} /> : null}
                          {d.outAt ? <TimePill kind="out" time={d.outAt} /> : null}
                        </View>
                        {!d.inAt ? <AppText variant="bodySm" tone="tertiary">{t('at_absent')}</AppText> : null}
                      </View>
                    </View>
                  ))}
                </View>
              </Card>
            ))}
          </View>
        )}
      </Screen>
    </Screen>
  );
}
